import { withFraudContext } from '@src/modules/forter/fraud-context.helper'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { PaymentMethod } from '@src/quince-site-integrated/src/templates/Account/Payments'

import { unifiedPaymentGatewayClient, unifiedPaymentGatewayHttpClient } from '../../utils/HttpClient'
import {
  AuthorizePaymentRequestDTO,
  PaymentSourceInput,
  UnifiedGatewaySession,
} from '../payment-service/payment-service.types'

import { UnifiedPaymentGatewayError } from './unified-payment-gateway.error'

export interface SessionCreateRequest {
  checkoutId: string
}

const X_INGRESS_VALUES = {
  HEADER: 'x-ingress',
  PDP: 'PDP',
}

class UnifiedPaymentGatewayService {
  /**
   * Fetch saved cards for the user.
   */
  async getSavedCards(): Promise<PaymentMethod[]> {
    try {
      const response = await unifiedPaymentGatewayClient.get(
        '/unified-payment-gateway-service/unified-payment-gateway/saved-cards',
        {
          params: {
            enrichActiveSubscriptions: true,
          },
        },
      )
      return response.data
    } catch (error) {
      console.error('Failed to fetch saved cards:', error)
      throw error
    }
  }

  /**
   * Delete a saved card by ID.
   * @param cardId The ID of the card to delete.
   */
  async deleteSavedCard(cardId: number) {
    try {
      const response = await unifiedPaymentGatewayClient.delete(
        `/unified-payment-gateway-service/saved-cards/${cardId}`,
      )
      return response.data
    } catch (error) {
      console.error('Failed to delete saved card:', error)
      throw error
    }
  }

  async createPaymentGatewaySession(checkoutId: string, checkoutAuthCode: string, abortSignal?: AbortSignal) {
    try {
      const headers = {
        ...{ [X_INGRESS_VALUES.HEADER]: X_INGRESS_VALUES.PDP },
        'checkout-auth-code': checkoutAuthCode,
      }
      const { data: unifiedGatewaySession } = await unifiedPaymentGatewayHttpClient.post<
        SessionCreateRequest,
        UnifiedGatewaySession
      >(
        '/unified-payment-gateway-service/unified-payment-gateway/sessions',
        {
          checkoutId,
        },
        {
          signal: abortSignal,
          headers,
        },
      )

      return unifiedGatewaySession
    } catch (e) {
      console.error('Failed to create payment gateway session:', e)
      throw e
    }
  }

  async updatePaymentSourceInput(sessionId: number, paymentSourceMetaData: PaymentSourceInput) {
    try {
      const { data: unifiedGatewaySession } = await unifiedPaymentGatewayHttpClient.patch<
        PaymentSourceInput,
        UnifiedGatewaySession
      >(
        `/unified-payment-gateway-service/unified-payment-gateway/sessions/${sessionId}/payment-source-input`,
        paymentSourceMetaData,
      )
      return unifiedGatewaySession
    } catch (e) {
      throw UnifiedPaymentGatewayError.from(e)
    }
  }

  async authorizePayment(payload: AuthorizePaymentRequestDTO, sessionId?: number) {
    try {
      if (!sessionId) {
        throw new Error('Session ID is required')
      }
      const isForterEnabled = getUserAgnosticGlobalFeatureGatesFromMemory().enable_forter_fraud_check
      const { data } = await unifiedPaymentGatewayHttpClient.post(
        `/unified-payment-gateway-service/unified-payment-gateway/sessions/${sessionId}/authorize`,
        // Attach Forter fraud signals only when the kill-switch gate is on (fail-safe/fail-open: unchanged
        // when disabled or no device token). One injection here covers every method that authorizes via UPG.
        withFraudContext(payload, isForterEnabled),
      )
      return data
    } catch (e) {
      throw UnifiedPaymentGatewayError.from(e)
    }
  }

  async updateUpgSessionAmount(sessionId: number, abortSignal?: AbortSignal) {
    try {
      const { data: unifiedGatewaySession } = await unifiedPaymentGatewayHttpClient.patch(
        `/unified-payment-gateway-service/unified-payment-gateway/sessions/${sessionId}/amount`,
        undefined,
        {
          signal: abortSignal,
        },
      )
      return unifiedGatewaySession
    } catch (e) {
      console.error('Failed to update UPG session amount:', e)
      throw e
    }
  }
}

const unifiedPaymentGatewayService = new UnifiedPaymentGatewayService()
export default unifiedPaymentGatewayService
